import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import TimelineStep from "./TimelineStep";
import TimelineSummary from "./TimelineSummary";
import ProductTable from "./ProductTable";
import { Wizad } from "./wizard";
import { mapEtapaToSegment } from "@/lib/utils";
import ProductTableScheduled from "./Scheduled-Order/ProductTableScheduled";
import ProfessorStepContent from "./ProfessorStepContent";
import type { CouponValidation } from "@/lib/api";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

interface OrderTimelineProps {
  orderItems: OrderItem[];
  updateQuantidade: (index: number, quantidade: number) => void;
  updateQuantidadesBatch: (updates: Array<{ id: number; quantidade: number }>) => void; // New prop
  formatCurrency: (value: number) => string;
  finalizarPedido: (reason?: string, coupon?: CouponValidation | null) => void;
  getGrossSubtotal: () => number;
  getSubtotal: () => number;
  getTotal: () => number;
  freteAnual: number;
  clientHasOrders?: boolean;
  freteSemestral: number;
  shippingFee?: number;
  isScheduled: boolean; // Added isScheduled
  isSimulated?: boolean; // Added isSimulated
  isBackofficeEdit?: boolean;
  isEditMode?: boolean;
  onAnnualInstallmentsChange?: (value: number) => void;
  onSemestralInstallmentsChange?: (value: number) => void;
  onAnnualConditionChange?: (value: string | null) => void;
  onSemestralConditionChange?: (value: string | null) => void;
  cycleYear?: number;
  isReadOnly?: boolean; // Ciclo em fechamento: só visualização, sem alterar nem finalizar
  shippingAddressSlot?: React.ReactNode; // Endereço de entrega, repassado ao resumo de valores
}

const OrderTimeline = ({
  orderItems,
  updateQuantidade,
  updateQuantidadesBatch, // New prop
  formatCurrency,
  finalizarPedido,
  getGrossSubtotal,
  getSubtotal,
  getTotal,
  clientHasOrders = true,
  freteAnual,
  freteSemestral,
  shippingFee,
  isScheduled, // Destructure isScheduled
  isSimulated = false, // Destructure isSimulated
  isBackofficeEdit = false,
  isEditMode = false,
  onAnnualInstallmentsChange,
  onSemestralInstallmentsChange,
  onAnnualConditionChange,
  onSemestralConditionChange,
  cycleYear = 2026,
  isReadOnly = false,
  shippingAddressSlot,
}: OrderTimelineProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Reset progress when the set of products changes or order type changes
  const itemIds = useMemo(() => orderItems.map(item => item.id).join(','), [orderItems]);

  React.useEffect(() => {
    setActiveStep(0);
    setCompletedSteps(new Set());
  }, [itemIds, isScheduled]);

  // Get segment order function
  const getSegmentOrder = (segmento: string | null): number => {
    const order: { [key: string]: number } = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      PV: 5,
    };
    return order[segmento || ""] || 999;
  };

  // Get unique segments with products, sorted by segment order
  const availableSegments = useMemo(() => {
    const segments = Array.from(
      new Set(orderItems.map((item) => mapEtapaToSegment(item.serie)))
    ).sort((a, b) => getSegmentOrder(a) - getSegmentOrder(b));

    return segments;
  }, [orderItems]);

  // Steps: segments + Professor + Resumo
  const totalSteps = availableSegments.length + 2;
  const isInProfessorStep = activeStep === availableSegments.length;
  const isInSummary = activeStep === availableSegments.length + 1;

  // Get items for current segment
  const currentSegmentItems = useMemo(() => {
    if (isInSummary || isInProfessorStep) return orderItems;

    const currentSegment = availableSegments[activeStep];
    return orderItems.filter(
      (item) => mapEtapaToSegment(item.serie) === currentSegment
    );
  }, [activeStep, availableSegments, orderItems, isInSummary, isInProfessorStep]);

  // Get professor items for the Professor step
  const professorItems = useMemo(() => {
    return orderItems.filter(
      (item) => item.utilizacao?.toLowerCase() === "professor"
    );
  }, [orderItems]);

  // Navigation functions
  const goToStep = (stepIndex: number) => {
    if (stepIndex !== activeStep) {
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
    }
    setActiveStep(stepIndex);
  };

  const goToNext = () => {
    if (activeStep < totalSteps - 1) {
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
      setActiveStep(activeStep + 1);
    }
  };

  const goToPrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Map global index to segment index for updateQuantidade
  const updateSegmentQuantity = (segmentIndex: number, quantidade: number) => {
    const item = currentSegmentItems[segmentIndex];
    const globalIndex = orderItems.findIndex(
      (globalItem) => globalItem.id === item.id
    );
    if (globalIndex !== -1) {
      updateQuantidade(globalIndex, quantidade);
    }
  };

  return (
    <div className="space-y-6">
      {/* Timeline Header */}
      <Wizad
        availableSegments={availableSegments}
        activeStep={activeStep}
        totalSteps={totalSteps}
        completedSteps={completedSteps}
        goToPrevious={goToPrevious}
        goToNext={goToNext}
        goToStep={goToStep}
      />

      {/* Content Area */}
      {isInSummary ? (
        <TimelineSummary
          cycleYear={cycleYear}
          orderItems={orderItems}
          isScheduled={isScheduled}
          fullOrderItems={orderItems}
          formatCurrency={formatCurrency}
          finalizarPedido={finalizarPedido}
          getGrossSubtotal={getGrossSubtotal}
          getSubtotal={getSubtotal}
          getTotal={getTotal}
          freteAnual={freteAnual} // Pass freteAnual
          freteSemestral={freteSemestral} // Pass freteSemestral
          shippingFee={shippingFee}
          isBackofficeEdit={isBackofficeEdit}
          isEditMode={isEditMode}
          onAnnualInstallmentsChange={onAnnualInstallmentsChange}
          onSemestralInstallmentsChange={onSemestralInstallmentsChange}
          onAnnualConditionChange={onAnnualConditionChange}
          onSemestralConditionChange={onSemestralConditionChange}
          isReadOnly={isReadOnly}
          shippingAddressSlot={shippingAddressSlot}
        />
      ) : (
        // `fieldset disabled` trava nativamente todos os steppers/inputs de quantidade dos cards
        // sem propagar a flag por cada componente; os gatilhos de expandir série são <div>, então
        // a navegação e a visualização continuam funcionando no modo somente leitura.
        <fieldset disabled={isReadOnly} className="m-0 min-w-0 border-0 p-0">
          {isInProfessorStep ? (
            <ProfessorStepContent
              cycleYear={cycleYear}
              orderItems={professorItems}
              fullOrderItems={orderItems}
              updateQuantidade={updateQuantidade}
              updateQuantidadesBatch={updateQuantidadesBatch}
              formatCurrency={formatCurrency}
              isScheduled={isScheduled}
              isSimulated={isSimulated}
            />
          ) : clientHasOrders ? (
            <ProductTableScheduled
              orderItems={currentSegmentItems}
              fullOrderItems={orderItems} // New prop
              updateQuantidade={updateSegmentQuantity}
              updateQuantidadesBatch={updateQuantidadesBatch} // New prop
              formatCurrency={formatCurrency}
              clientHasOrders={clientHasOrders}
              isScheduled={isScheduled}
              isSimulated={isSimulated}
              cycleYear={cycleYear}
            // getDisplayQuantity={getDisplayQuantity}
            />
          ) : (
            <ProductTable
              orderItems={currentSegmentItems}
              fullOrderItems={orderItems} // New prop
              updateQuantidade={updateSegmentQuantity}
              updateQuantidadesBatch={updateQuantidadesBatch} // New prop
              formatCurrency={formatCurrency}
              shippingFee={shippingFee}
              cycleYear={cycleYear}
            />
          )}
        </fieldset>
      )}

      <Wizad
        availableSegments={availableSegments}
        activeStep={activeStep}
        totalSteps={totalSteps}
        completedSteps={completedSteps}
        goToPrevious={goToPrevious}
        goToNext={goToNext}
        goToStep={goToStep}
      />
    </div>
  );
};

export default OrderTimeline;
